// src/lib/doctorIntroPreview.js

import {
  assertHydrationOwner,
  reportHydrationMismatch,
} from "@/lib/hydrationIsolationTelemetry.js";

const KEY = "afrimed_doctor_intro_preview_v1";

function scopedKey(userId = "") {
  const uid = String(userId || "").trim();
  return uid ? `${KEY}:${uid}` : KEY;
}

function isDoctorProfileRoute() {
  if (typeof window === "undefined") return false;
  const path = String(window.location.pathname || "");
  return path.startsWith("/settings/doctor") || path.startsWith("/doctor/profile");
}

const EMPTY = {
  // Raw form fields (existing)
  name: "",
  dobIso: "",
  specialtySummary: "",
  registerNumber: "",
  registerMeta: "",
  about: "",
  formations: [], // [{ id, title, subtitle }]
  languages: [],  // [{ id, label, level }]

  // Normalised fields for DoctorProfile / mapping
  full_name: "",
  primary_specialty: "",
  about_you: "",
  focus_tags: [], // array of strings
};

export function loadDoctorIntroPreview(userId = "") {
  if (typeof window === "undefined") return { ...EMPTY };
  try {
    if (!String(userId || "").trim() && isDoctorProfileRoute()) {
      reportHydrationMismatch({
        authUserId: "",
        hydratedOwnerId: "",
        source: "doctorIntroPreview.localStorage",
        role: "doctor",
        profileType: "doctor",
        mismatchType: "unscoped_preview_read",
      });
    }
    const key = scopedKey(userId);
    const raw = window.localStorage.getItem(key);
    if (!raw) return { ...EMPTY };

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return { ...EMPTY };
    const savedOwnerId = String(parsed.__ownerUserId || parsed.ownerUserId || "").trim();
    if (
      savedOwnerId &&
      String(userId || "").trim() &&
      !assertHydrationOwner({
        authUserId: userId,
        hydratedOwnerId: savedOwnerId,
        source: "doctorIntroPreview.localStorage",
        role: "doctor",
        profileType: "doctor",
        mismatchType: "preview_owner_mismatch",
      })
    ) {
      return { ...EMPTY };
    }

    const formations = Array.isArray(parsed.formations)
      ? parsed.formations
      : [];

    const languages = Array.isArray(parsed.languages)
      ? parsed.languages
      : [];

    // ---- Normalisation for DoctorProfile ----
    const full_name = (
      parsed.full_name ||
      parsed.name ||
      ""
    ).trim();

    const primary_specialty = (
      parsed.primary_specialty ||
      parsed.specialty ||
      parsed.specialtySummary ||
      ""
    ).trim();

    const about_you = (
      parsed.about_you ||
      parsed.about ||
      ""
    ).trim();

    let focus_tags = [];
    if (Array.isArray(parsed.focus_tags)) {
      focus_tags = parsed.focus_tags;
    } else if (Array.isArray(parsed.focus)) {
      focus_tags = parsed.focus;
    } else if (typeof parsed.focus_tags === "string") {
      focus_tags = parsed.focus_tags
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }

    return {
      ...EMPTY,
      ...parsed,
      formations,
      languages,
      full_name,
      primary_specialty,
      about_you,
      focus_tags,
    };
  } catch {
    return { ...EMPTY };
  }
}

export function saveDoctorIntroPreview(patch, userId = "") {
  if (typeof window === "undefined") return { ...EMPTY };
  const uid = String(userId || "").trim();
  if (!uid && isDoctorProfileRoute()) {
    reportHydrationMismatch({
      source: "doctorIntroPreview.localStorage",
      role: "doctor",
      profileType: "doctor",
      mismatchType: "unscoped_preview_write",
    });
  }

  const key = scopedKey(uid);
  const current = loadDoctorIntroPreview(uid);
  const next = { ...current, ...patch, ...(uid ? { __ownerUserId: uid } : {}) };

  window.localStorage.setItem(key, JSON.stringify(next));
  return next;
}
